/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { flooringAreaValueNamePresenter } from '@kaiizen/kai-presenters';
import { getGroupMeasurementTotals, groupHasRoomAreaTemplate, type ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

import { formatWholeMeasurement, getFlooringAreaTotals } from '@/Client/Lib/roomGroupMeasurementsLib';
import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { pxToRem } from '@/Client/Styles/pxToRem';
import { type FlooringHatchPattern, FlooringHatchSwatch } from './FlooringHatchSwatch';

const CARD_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_CARD_STYLE;
  background: ${COLORS.white};
  border: 1px solid ${COLORS.stroke};
  padding: 20px 24px 22px;
  margin-bottom: 22px;
`;

const TITLE_ROW_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_TITLE_ROW_STYLE;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
`;

const TITLE_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_TITLE_STYLE;
  font-family: ${FONT.display};
  font-size: ${pxToRem(28)};
  font-weight: 700;
  letter-spacing: -0.015em;
  line-height: 1.1;
  color: ${COLORS.ink};
  margin: 0;
`;

const VIEW_TABLE_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_VIEW_TABLE_STYLE;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 11px;
  border: 0;
  background: ${COLORS.accent};
  color: ${COLORS.ink};
  font-family: ${FONT.mono};
  font-size: ${pxToRem(10)};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    background: ${COLORS.accentDeep};
  }
`;

/**
 * Both tiers sit together on the left, divided by a hairline — pushing the
 * supporting metrics to the far edge would read as two unrelated groups.
 */
const METRIC_BAND_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_METRIC_BAND_STYLE;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 26px;
  flex-wrap: wrap;
`;

const LEAD_GROUP_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_LEAD_GROUP_STYLE;
  display: flex;
  gap: 44px;
  flex-wrap: wrap;
`;

const SUPPORT_GROUP_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_SUPPORT_GROUP_STYLE;
  display: flex;
  align-items: flex-end;
  align-self: stretch;
  gap: 34px;
  flex-wrap: wrap;
  padding-bottom: 3px;
  padding-left: 26px;
  border-left: 1px solid ${COLORS.stroke};
`;

const STAT_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_STAT_STYLE;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

/** Regular weight, so the figures are the only bold thing in the band. */
const STAT_LABEL_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_STAT_LABEL_STYLE;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9.5)};
  font-weight: 500;
  letter-spacing: 0.11em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
  white-space: nowrap;
`;

const LEAD_LABEL_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_LEAD_LABEL_STYLE;
  color: ${COLORS.ink};
`;

const STAT_VALUE_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_STAT_VALUE_STYLE;
  font-family: ${FONT.body};
  font-weight: 700;
  line-height: 1.05;
  letter-spacing: -0.02em;
  color: ${COLORS.ink};
  white-space: nowrap;
`;

const LEAD_VALUE_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_LEAD_VALUE_STYLE;
  font-size: ${pxToRem(20)};
`;

const SUPPORT_VALUE_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_SUPPORT_VALUE_STYLE;
  font-size: ${pxToRem(15)};
`;

const UNIT_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_UNIT_STYLE;
  font-family: ${FONT.body};
  font-weight: 500;
  letter-spacing: 0;
  color: ${COLORS.midtone};
  margin-left: 4px;
`;

const LEAD_UNIT_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_LEAD_UNIT_STYLE;
  font-size: ${pxToRem(11)};
`;

const SUPPORT_UNIT_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_SUPPORT_UNIT_STYLE;
  font-size: ${pxToRem(10)};
`;

/** Spacing alone separates the breakdown; a rule here doubles the band's own edge. */
const CHIPS_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_CHIPS_STYLE;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 22px;
`;

const CHIP_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_CHIP_STYLE;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 12px 7px 8px;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.sunken};
`;

const CHIP_TEXT_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_CHIP_TEXT_STYLE;
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const CHIP_LABEL_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_CHIP_LABEL_STYLE;
  font-family: ${FONT.mono};
  font-size: ${pxToRem(9)};
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.midtone};
`;

const CHIP_VALUE_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_CHIP_VALUE_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  font-weight: 700;
  letter-spacing: -0.01em;
  color: ${COLORS.ink};
  white-space: nowrap;
`;

const CHIP_UNIT_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_CHIP_UNIT_STYLE;
  font-size: ${pxToRem(9.5)};
`;

const NONE_SPECIFIED_STYLE = css`
  label: FLOOR_PLAN_OVERVIEW_NONE_SPECIFIED_STYLE;
  font-family: ${FONT.body};
  font-size: ${pxToRem(12.5)};
  font-weight: 400;
  font-style: italic;
  color: ${COLORS.midtone};
`;

/**
 * A measured figure with its unit trailing quietly behind it. Nothing measured
 * is an em dash, never a zero — a zero would claim the room has none of this,
 * where the truth is that nobody has measured it.
 */
const MeasurementFigure = ({
  unit,
  unitStyle,
  value,
}: {
  unit: string;
  unitStyle: ReturnType<typeof css>;
  value: number | null | undefined;
}) =>
  value
    ? (
      <>
        {formatWholeMeasurement(value)}
        <span css={[UNIT_STYLE, unitStyle]}>{unit}</span>
      </>
    )
    : <>—</>;

const OverviewStat = ({
  label,
  lead,
  unit,
  value,
}: {
  label: string;
  lead?: boolean;
  unit: string;
  value: number | null | undefined;
}) => (
  <div css={STAT_STYLE}>
    <span css={[STAT_LABEL_STYLE, lead && LEAD_LABEL_STYLE]}>{label}</span>
    <span css={[STAT_VALUE_STYLE, lead ? LEAD_VALUE_STYLE : SUPPORT_VALUE_STYLE]}>
      <MeasurementFigure
        unit={unit}
        unitStyle={lead ? LEAD_UNIT_STYLE : SUPPORT_UNIT_STYLE}
        value={value}
      />
    </span>
  </div>
);

const FlooringChip = ({
  area,
  pattern,
  valueName,
}: {
  area: number;
  pattern: FlooringHatchPattern;
  valueName: string;
}) => (
  <div css={CHIP_STYLE}>
    <FlooringHatchSwatch pattern={pattern} />
    <span css={CHIP_TEXT_STYLE}>
      <span css={CHIP_LABEL_STYLE}>{flooringAreaValueNamePresenter(valueName)}</span>
      <span css={CHIP_VALUE_STYLE}>
        <MeasurementFigure unit='sqft' unitStyle={CHIP_UNIT_STYLE} value={area} />
      </span>
    </span>
  </div>
);

/**
 * Scope-level totals above the room index, in three tiers: the headline areas,
 * the supporting spans behind a hairline, then the flooring breakdown — a
 * subdivision of the Flooring total rather than a second rank of peers.
 * Measured area sits beside the property's listed square footage so the drawn
 * rooms can be read against what the listing claims.
 */
export const FloorPlanOverviewHeader = ({
  flooringHatchPatterns,
  flooringValueNames,
  groups,
  listedSqft,
  onViewTable,
}: {
  flooringHatchPatterns: Record<string, FlooringHatchPattern>;
  flooringValueNames: readonly string[];
  groups: readonly ScanGroupOptimized[];
  /** The property's listed square footage, shown against the measured total. */
  listedSqft?: number | null;
  onViewTable?: () => void;
}) => {
  const roomGroups = groups.filter(groupHasRoomAreaTemplate);
  if (!roomGroups.length) return null;
  const { area, perimeter, wallArea } = getGroupMeasurementTotals(roomGroups);
  const flooringTotals = getFlooringAreaTotals(roomGroups, flooringValueNames);
  const measuredFlooring = flooringValueNames.flatMap(valueName => {
    const flooringArea = flooringTotals[valueName];
    return flooringArea == null ? [] : [{ area: flooringArea, valueName }];
  });
  const flooringTotal = measuredFlooring.reduce((sum, flooring) => sum + flooring.area, 0);
  return (
    <div css={CARD_STYLE} data-testid='floor-plan-overview-header'>
      <div css={TITLE_ROW_STYLE}>
        <h2 css={TITLE_STYLE}>Project Measurements</h2>
        {onViewTable && (
          <button type='button' css={VIEW_TABLE_STYLE} onClick={onViewTable}>
            View table
            <svg viewBox='0 0 14 14' width='12' height='12' fill='none' aria-hidden='true'>
              <path
                d='M3 11L11 3M11 3H5M11 3V9'
                stroke='currentColor'
                strokeWidth='1.4'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
        )}
      </div>
      <div css={METRIC_BAND_STYLE}>
        <div css={LEAD_GROUP_STYLE}>
          {/* A zero-square-foot listing is a missing listing, not a zero-size property. */}
          <OverviewStat label='Listed area' lead unit='sqft' value={listedSqft} />
          <OverviewStat label='Measured area' lead unit='sqft' value={area} />
        </div>
        <div css={SUPPORT_GROUP_STYLE}>
          <OverviewStat label='Perimeter' unit='ft' value={perimeter} />
          <OverviewStat label='Walls' unit='sqft' value={wallArea} />
          <OverviewStat label='Flooring' unit='sqft' value={flooringTotal} />
        </div>
      </div>
      <div css={CHIPS_STYLE} data-testid='floor-plan-overview-flooring-chips'>
        {measuredFlooring.length
          ? measuredFlooring.map(({ area: flooringArea, valueName }) => (
            <FlooringChip
              key={valueName}
              area={flooringArea}
              pattern={flooringHatchPatterns[valueName]}
              valueName={valueName}
            />
          ))
          : (
            <div css={CHIP_STYLE}>
              <span css={NONE_SPECIFIED_STYLE}>None specified</span>
            </div>
          )}
      </div>
    </div>
  );
};
